import moment from "moment";
import "moment/locale/es";

/*
  Columnas del Flujo De Efectivo Diario.

  El backend siempre manda un valor por DIA. Agrupar por semana o por mes se hace
  aqui, sin volver a consultar, y lo usan igual la tabla y el Excel:
    - dato / total / informativo: se suman los dias del grupo;
    - saldo (remanente, saldo en bancos, diferencia): el ultimo dia del grupo que
      tenga valor, porque un saldo no se suma.
  El remanente del grupo sigue siendo "anterior + ingresos - egresos" del grupo, asi
  que la formula del Excel funciona igual con cualquier agrupacion.
*/

export const MODOS_COLUMNA = [
  { valor: "dia", etiqueta: "Por dia" },
  { valor: "semana", etiqueta: "Por semana" },
  { valor: "mes", etiqueta: "Por mes" },
];

export const ETIQUETA_TIPO_COLUMNA = {
  real: "Real",
  hoy: "Hoy",
  proyectado: "Proyectado",
};

const capitalizar = (texto) => (texto ? texto.charAt(0).toUpperCase() + texto.slice(1) : "");

const diaMoment = (fecha) => moment(fecha, "YYYY-MM-DD").locale("es");

// Una columna que junta dias reales y proyectados (la semana o el mes de hoy) se
// marca como "hoy": tiene de los dos.
const tipoDeGrupo = (tipos) => {
  if (tipos.every((t) => t === "real")) return "real";
  if (tipos.every((t) => t === "proyectado")) return "proyectado";
  return "hoy";
};

export const construirColumnas = (dias, modo) => {
  const grupos = [];
  const porLlave = new Map();
  (dias || []).forEach((dia, indice) => {
    const m = diaMoment(dia.fecha);
    let llave = dia.fecha;
    if (modo === "semana") llave = m.clone().startOf("isoWeek").format("YYYY-MM-DD");
    if (modo === "mes") llave = m.format("YYYY-MM");
    if (!porLlave.has(llave)) {
      const grupo = { llave, indices: [], fechas: [], tipos: [] };
      porLlave.set(llave, grupo);
      grupos.push(grupo);
    }
    const grupo = porLlave.get(llave);
    grupo.indices.push(indice);
    grupo.fechas.push(dia.fecha);
    grupo.tipos.push(dia.tipo);
  });

  return grupos.map((grupo) => {
    const inicio = diaMoment(grupo.fechas[0]);
    const fin = diaMoment(grupo.fechas[grupo.fechas.length - 1]);
    let titulo = capitalizar(inicio.format("dddd"));
    let subtitulo = inicio.format("DD/MM/YYYY");
    if (modo === "semana") {
      titulo = `Semana ${inicio.isoWeek()}`;
      subtitulo = `${inicio.format("DD/MM")} - ${fin.format("DD/MM/YYYY")}`;
    }
    if (modo === "mes") {
      titulo = capitalizar(inicio.format("MMMM YYYY"));
      subtitulo = `${inicio.format("DD/MM")} - ${fin.format("DD/MM/YYYY")}`;
    }
    return {
      key: grupo.llave,
      titulo,
      subtitulo,
      // Solo por dia: el Excel escribe la fecha como fecha real, no como texto.
      fecha: modo === "semana" || modo === "mes" ? null : grupo.fechas[0],
      tipo: tipoDeGrupo(grupo.tipos),
      indices: grupo.indices,
      fechas: grupo.fechas,
    };
  });
};

const redondear = (valor) => Math.round(valor * 100) / 100;

export const agruparRenglones = (renglones, columnas) =>
  (renglones || []).map((renglon) => {
    if (!renglon.valores) return renglon;
    const valores = columnas.map((columna) => {
      if (renglon.tipo === "saldo") {
        // La diferencia contra bancos solo existe en dias cerrados. En una semana o mes
        // que junta dias reales y proyectados, el remanente es el del ultimo dia
        // (proyectado) y ya no es comparable contra el saldo del banco.
        if (renglon.clave === "rem_diferencia" && columna.tipo !== "real") return null;
        for (let k = columna.indices.length - 1; k >= 0; k--) {
          const valor = renglon.valores[columna.indices[k]];
          if (valor !== null && valor !== undefined) return valor;
        }
        return null;
      }
      let suma = 0;
      let hayValor = false;
      columna.indices.forEach((indice) => {
        const valor = renglon.valores[indice];
        if (valor === null || valor === undefined) return;
        suma += Number(valor) || 0;
        hayValor = true;
      });
      return hayValor ? redondear(suma) : null;
    });
    return { ...renglon, valores };
  });

/**
 * Renglones "hoja" de un renglon con su signo: un dato es el mismo; un total son sus
 * componentes, recursivamente ("-iva_acreditar" entra restando). Sirve para que el
 * detalle de una celda de total sume exactamente lo que dice la celda.
 */
export const hojasConSigno = (renglon, porClave, signo = 1) => {
  if (!renglon) return [];
  if (renglon.tipo !== "total") return [{ clave: renglon.clave, signo }];
  return (renglon.componentes || []).flatMap((componente) => {
    const resta = componente.startsWith("-");
    const hijo = porClave.get(resta ? componente.slice(1) : componente);
    return hojasConSigno(hijo, porClave, resta ? -signo : signo);
  });
};

// Se puede abrir el detalle de los importes que vienen de movimientos; los saldos
// son acumulados y no tienen un detalle propio.
export const tieneDetalle = (renglon) =>
  renglon && (renglon.tipo === "dato" || renglon.tipo === "informativo" || renglon.tipo === "total");

export const movimientosDeCelda = (movimientos, renglon, columna, porClave) => {
  const signos = new Map();
  hojasConSigno(renglon, porClave).forEach(({ clave, signo }) => signos.set(clave, (signos.get(clave) || 0) + signo));
  const fechas = new Set(columna.fechas);
  return (movimientos || [])
    .filter((m) => signos.has(m.clave) && fechas.has(m.fecha))
    .map((m) => ({ ...m, montoEnCelda: redondear(m.monto * signos.get(m.clave)) }));
};

export const ETIQUETA_ORIGEN = {
  real: "Real",
  proyectado: "Proyectado",
  presupuesto: "Presupuesto",
  programado: "Programado",
};

// Codigos de las cuentas configuradas de un renglon, para las columnas "Cuenta".
export const textoCuentas = (renglon) => ((renglon && renglon.cuentas) || []).map((c) => c.codigo).join(", ");
